import React, { useEffect, useState } from "react";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import UsernameDialog from "@/components/UsernameDialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLocation } from "react-router-dom";

const LIGHT_WORKSPACE = "radial-gradient(circle at 86% 8%, rgba(248,120,31,.12), transparent 30%), radial-gradient(circle at 10% 82%, rgba(14,159,160,.08), transparent 34%), linear-gradient(135deg, #fffaf5 0%, #faf9f6 48%, #f3f7f5 100%)";
const DARK_WORKSPACE = "radial-gradient(circle at 86% 8%, rgba(248,120,31,.10), transparent 30%), radial-gradient(circle at 10% 82%, rgba(14,159,160,.07), transparent 34%), linear-gradient(135deg, #11171b 0%, #10161a 48%, #0e1818 100%)";
const PUBLIC_CHAT_LIGHT_WORKSPACE = "#faf9f6";
const PUBLIC_CHAT_DARK_WORKSPACE = "#101619";

function GalleryLightbox() {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    const onClick = (event) => {
      const image = event.target instanceof HTMLImageElement ? event.target : null;
      if (!image || image.alt !== "") return;
      if (!image.closest(".aspect-square")) return;
      setSrc(image.currentSrc || image.src);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (!src) return undefined;
    const onKey = (event) => { if (event.key === "Escape") setSrc(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [src]);

  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setSrc(null)} role="dialog" aria-modal="true" aria-label="View profile photo">
      <img src={src} alt="Profile photo" className="max-h-[90vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <button type="button" onClick={() => setSrc(null)} className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-xl text-slate-800 shadow-lg" aria-label="Close photo">×</button>
    </div>
  );
}

export default function AppShell({ children, rightRail = null, fullBleed = false }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const isPublicChat = location.pathname === "/public-chat";
  const isMessages = location.pathname === "/messages";
  const needsUsername = user && user.needs_username;

  const renderedChildren = fullBleed && React.isValidElement(children)
    ? React.cloneElement(children, {
        style: {
          ...(children.props.style || {}),
          background: isPublicChat ? (theme === "dark" ? PUBLIC_CHAT_DARK_WORKSPACE : PUBLIC_CHAT_LIGHT_WORKSPACE) : (theme === "dark" ? DARK_WORKSPACE : LIGHT_WORKSPACE), backgroundImage: "none",
        },
      })
    : children;

  return (
    <div className={`${isMessages ? "h-[100vh] overflow-hidden lg:h-[90.91vh]" : "min-h-screen"} bg-background ${isPublicChat ? "chat-page-shell" : ""} ${isMessages ? "messages-page-shell" : ""}`}>
      <Sidebar />
      <div className={`lg:pl-64 ${isMessages ? "messages-app-column h-full min-h-0 overflow-hidden" : ""}`}>
        <div className={isPublicChat ? "relative z-[100]" : ""}><TopBar /></div>
        <div className={`chat-page-workspace flex min-h-0 overflow-hidden ${isMessages ? "h-[calc(100vh-65px)] lg:h-[calc(90.91vh-65px)]" : ""} ${fullBleed ? (isPublicChat ? "fixed inset-x-0 bottom-0 top-[64px] h-auto bg-transparent lg:absolute lg:inset-x-0 lg:bottom-0 lg:top-[64px]" : "h-[calc(100vh-65px)] bg-transparent") : "lg:mt-3 lg:rounded-t-[32px] lg:border lg:border-b-0 lg:border-white/70 lg:shadow-[0_-10px_40px_rgba(15,23,42,0.05)] dark:lg:border-white/10"}`} style={fullBleed ? { background: isPublicChat ? (theme === "dark" ? PUBLIC_CHAT_DARK_WORKSPACE : PUBLIC_CHAT_LIGHT_WORKSPACE) : (theme === "dark" ? DARK_WORKSPACE : LIGHT_WORKSPACE), backgroundImage: "none" } : undefined}>
          <main className={`chat-page-main min-w-0 min-h-0 flex-1 ${isMessages ? "h-full overflow-hidden p-0" : ""} ${fullBleed ? "h-full bg-transparent p-0" : "p-4 sm:p-6 lg:p-8"}`}>
            <div className={`${isMessages || fullBleed ? "h-full min-h-0" : ""} ${fullBleed ? "max-w-none space-y-0" : "mx-auto max-w-5xl animate-fade-up space-y-6 sm:space-y-8"}`}>
              {renderedChildren}
            </div>
          </main>
          {rightRail && (
            <aside className={`sticky top-[65px] hidden h-[calc(100vh-65px)] w-80 shrink-0 overflow-y-auto border-l border-slate-100 p-5 no-scrollbar xl:block dark:border-white/10 ${fullBleed ? "space-y-4 bg-transparent" : "space-y-6 bg-white dark:bg-[#111116]"}`} style={fullBleed ? { background: theme === "dark" ? DARK_WORKSPACE : LIGHT_WORKSPACE } : undefined}>
              {rightRail}
            </aside>
          )}
        </div>
      </div>
      <UsernameDialog open={!!needsUsername} />
      <GalleryLightbox />
    </div>
  );
}
